import type { ColumnDef } from '@tanstack/vue-table';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { AnyItem } from '#layers/data-model';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { CollectionFieldPath } from '#layers/data-model/utils/collection-field-path';
import { createColumnHelper } from '@tanstack/vue-table';
import { computed, h, toValue } from 'vue';
import { RenderDisplayTemplate } from '#components';
import { getCollectionFieldPathLabels, resolveCollectionFieldPath, toCollectionFieldPathKey } from '#layers/data-model/utils/collection-field-path';
import { fieldPathToDisplayTemplateAst } from '#layers/data-model/utils/display-template';
import { useDisplayNames } from '~/composables/use-display-names';
import LayoutTableFieldPathLabel from './LayoutTableFieldPathLabel.vue';

export function useTableColumns<T extends AnyItem>(options: {
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection>;
	visibleColumnPaths: MaybeRefOrGetter<CollectionFieldPath[]>;
}): { fieldColumns: ComputedRef<ColumnDef<T, unknown>[]> } {
	const columnHelper = createColumnHelper<T>();

	const { getFieldDisplayName } = useDisplayNames();

	// Keep TanStack descriptor identity stable across column edits.
	const fieldColumnCache = new WeakMap<object, Map<string, ColumnDef<T, unknown>>>();

	function getCachedFieldColumn(fieldPath: CollectionFieldPath): ColumnDef<T, unknown> | null {
		const collection = toValue(options.collection);
		let perCollection = fieldColumnCache.get(collection);

		if (!perCollection) {
			perCollection = new Map<string, ColumnDef<T, unknown>>();
			fieldColumnCache.set(collection, perCollection);
		}

		const columnId = toCollectionFieldPathKey(fieldPath);
		const cached = perCollection.get(columnId);

		if (cached) return cached;

		const segments = resolveCollectionFieldPath(collection, fieldPath);
		const leafSegment = segments?.at(-1);

		if (!segments || !leafSegment) return null;

		const isSortablePrimitiveColumn = fieldPath.length === 1 && 'type' in leafSegment.field;

		const displayTemplate = fieldPathToDisplayTemplateAst(fieldPath);

		const descriptor = columnHelper.display({
			id: columnId,
			header: () =>
				h(LayoutTableFieldPathLabel, {
					labels: getCollectionFieldPathLabels(collection, fieldPath, getFieldDisplayName),
				}),
			enableSorting: isSortablePrimitiveColumn,
			enableHiding: true,
			enableOrdering: true,
			enablePinning: true,
			cell: ({ row }) =>
				h(RenderDisplayTemplate, {
					item: row.original,
					displayTemplate,
					collectionId: collection.id,
				}),
		});

		perCollection.set(columnId, descriptor);
		return descriptor;
	}

	const fieldColumns = computed(() => toValue(options.visibleColumnPaths).flatMap((fieldPath) => {
		const descriptor = getCachedFieldColumn(fieldPath);
		return descriptor ? [descriptor] : [];
	}));

	return { fieldColumns };
}
